import get from 'lodash/get';
import { Assets, uploadAsset } from 'modules/assets';
import { Audition } from 'modules/auditions';
import filter from 'lodash/filter';
import {
  fetchProducer,
  fetchProducerStats,
  postProducer,
  patchProducer,
  fetchProducerAuditions,
  fetchSharedProducer,
  searchProducers,
} from './api';
import {
  createAsyncThunk,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';

export const loadProducer = createAsyncThunk(
  'producers/load',
  async producerId => await fetchProducer(producerId),
);
export const loadSharedProducer = createAsyncThunk(
  'producers/loadShared',
  async token => await fetchSharedProducer(token),
);
export const loadProducerAuditions = createAsyncThunk(
  'producers/fetchProducerAuditions',
  async ({ filter, pageLimit }) =>
    await fetchProducerAuditions(filter, pageLimit),
);
export const loadProducerStats = createAsyncThunk(
  'producers/loadStats',
  async producerId => await fetchProducerStats(producerId),
);
export const createProducer = createAsyncThunk(
  'producers/create',
  async ({ firstName, lastName }) => await postProducer(firstName, lastName),
);
const update = createAsyncThunk(
  'producers/update',
  async ({ producerId, values }) => await patchProducer(producerId, values),
);

export function uploadProducerProfileImage(accountId, imageFile) {
  return dispatch => {
    const context = Assets.createAccountContext(accountId);
    return dispatch(
      uploadAsset({
        imageFile,
        assetType: 'image',
        assetClass: 'profileImage',
        context,
      }),
    );
  };
}

export function updateProducer(accountId, producerId, values) {
  return dispatch => {
    const profileImage = get(values, 'profileImage');

    if (profileImage) {
      return dispatch(uploadProducerProfileImage(accountId, profileImage)).then(
        response => {
          const producerValues = {
            ...values,
            profileImage: response.payload,
          };

          return dispatch(update({ producerId, values: producerValues }));
        },
      );
    }

    return dispatch(update({ producerId, values }));
  };
}

export const loadSearchedProducers = createAsyncThunk(
  'producers/loadSearched',
  async searchOptions => await searchProducers(searchOptions),
);

const producersSlice = createSlice({
  name: 'producers',
  initialState: {
    loading: false,
    producer: null,
    producerStats: null,
    searchedProducers: [],
  },
  extraReducers: builder => {
    builder
      .addCase(loadProducer.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadProducer.fulfilled, (state, action) => {
        state.loading = false;
        state.producer = action.payload.data;
      })
      .addCase(loadSharedProducer.fulfilled, (state, action) => {
        state.producer = action.payload.data;
      })
      .addCase(loadProducerAuditions.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadProducerAuditions.fulfilled, (state, action) => {
        state.loading = false;
        // TODO Ahab clinic (516) move this to auditions-producers module
        state.producerAuditions = action.payload.data;
      })
      .addCase(loadProducerStats.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadProducerStats.fulfilled, (state, action) => {
        state.loading = false;
        state.producerStats = action.payload.data;
      })
      .addCase(loadProducerStats.rejected, (state, _) => {
        state.loading = false;
      })
      .addCase(createProducer.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(createProducer.fulfilled, (state, action) => {
        state.loading = false;
        state.producer = action.payload.data;
      })
      .addCase(update.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(update.fulfilled, (state, action) => {
        state.loading = false;
        state.producer = action.payload.data;
      })
      .addCase(update.rejected, (state, _) => {
        state.loading = false;
      })
      .addCase(loadSearchedProducers.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadSearchedProducers.fulfilled, (state, action) => {
        state.loading = false;
        state.searchedProducers = action.payload.data;
      })
      .addCase(loadSearchedProducers.rejected, (state, _) => {
        state.loading = false;
      });
  },
  selectors: {
    getProducer: state => state.producer,
    getProducerId: state => state.producer.id,
    getProducerStats: state => state.producerStats,
    getSearchedProducers: state => state.searchedProducers,
    getProducersLoading: state => state.loading,
    getProducerAuditions: state => state.producerAuditions,
  },
});

export const reducer = producersSlice.reducer;
export const {
  getProducer,
  getProducerId,
  getProducerStats,
  getSearchedProducers,
  getProducersLoading,
  getProducerAuditions,
} = producersSlice.selectors;
export const getActiveAuditionsByProducerId = producerId =>
  createSelector(
    getProducerAuditions,
    auditions => {
      return filter(
        auditions,
        audition =>
          Audition.getAuditionStatus(audition) === Audition.STATUSES.ACTIVE &&
          audition.producerId === producerId,
      );
    },
  );
